import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Sparkles, Zap, Lock, Check } from 'lucide-react';

/**
 * Inline upgrade prompt shown when a dealer clicks on a feature they don't have access to.
 * Doesn't enforce any payment — it's a soft sales prompt. Admin enables via /admin → Plans.
 */
const COLOR_THEMES = {
  cyan: {
    bgSoft: 'bg-cyan-500/10 border-cyan-500/30',
    bgIcon: 'bg-cyan-500/20',
    icon: 'text-cyan-400',
    check: 'text-cyan-400',
    btn: 'bg-cyan-500 hover:bg-cyan-400 text-slate-950',
  },
  fuchsia: {
    bgSoft: 'bg-fuchsia-500/10 border-fuchsia-500/30',
    bgIcon: 'bg-fuchsia-500/20',
    icon: 'text-fuchsia-400',
    check: 'text-fuchsia-400',
    btn: 'bg-fuchsia-500 hover:bg-fuchsia-400 text-white',
  },
};

const FEATURE_PROMOS = {
  reels_enabled: {
    title: 'AI Reels — Grow Plan',
    icon: Sparkles,
    color: 'cyan',
    bullets: [
      'Generate Instagram & Facebook reels from your inventory',
      'AI captions + hashtags powered by GPT',
      'Branded templates (Premium Luxury, Fast Sell, Minimal, New Arrival)',
      'Direct MP4 download (9:16, Instagram-ready)',
    ],
    cta: 'Upgrade to Grow Plan',
  },
  marketplace_leads_enabled: {
    title: 'Marketplace Leads — DrivePro Plan',
    icon: Zap,
    color: 'fuchsia',
    bullets: [
      'Upload CSV leads from CarDekho, OLX & more',
      'AI-powered column normalization & deduplication',
      'Automated WhatsApp acknowledgments (Day 1, 2, 5)',
      'Source tracking + telecaller workflow',
    ],
    cta: 'Upgrade to DrivePro Plan',
  },
};

const UpgradeRequiredModal = ({ open, onOpenChange, feature }) => {
  const promo = FEATURE_PROMOS[feature] || {
    title: 'Upgrade Required',
    icon: Lock,
    color: 'cyan',
    bullets: ['This feature is part of a premium plan.'],
    cta: 'Contact us to upgrade',
  };
  const Icon = promo.icon;
  const theme = COLOR_THEMES[promo.color] || COLOR_THEMES.cyan;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="upgrade-required-modal"
        className="bg-slate-900 border-slate-700 text-white max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-amber-400" />
            Feature locked
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className={`flex items-center gap-3 p-4 rounded-xl border ${theme.bgSoft}`}>
            <div className={`w-12 h-12 rounded-full ${theme.bgIcon} flex items-center justify-center flex-shrink-0`}>
              <Icon className={`w-6 h-6 ${theme.icon}`} />
            </div>
            <div>
              <div className="font-bold text-white">{promo.title}</div>
              <div className="text-xs text-slate-400 mt-0.5">Unlock to activate this feature</div>
            </div>
          </div>

          <ul className="space-y-2">
            {promo.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                <Check className={`w-4 h-4 ${theme.check} mt-0.5 flex-shrink-0`} />
                <span>{b}</span>
              </li>
            ))}
          </ul>

          <div className="bg-slate-950/50 border border-slate-700/50 rounded-lg p-3 text-xs text-slate-400">
            Contact your Propitstop account manager or the admin team to activate this on your account.
            Once enabled, the feature will appear instantly — no logout required.
          </div>
        </div>

        <div className="flex gap-2 justify-end">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="upgrade-modal-close"
            className="border-slate-700 bg-slate-900/40 text-slate-200"
          >
            Maybe later
          </Button>
          <Button
            onClick={() => {
              const subject = `Upgrade request: ${promo.title}`;
              window.open(`mailto:support@propitstop.in?subject=${encodeURIComponent(subject)}`, '_blank');
            }}
            data-testid="upgrade-modal-contact"
            className={`${theme.btn} font-semibold`}
          >
            {promo.cta}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpgradeRequiredModal;
